import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import {
  FaFileInvoiceDollar, FaPaperPlane, FaSyncAlt, FaCheckCircle,
  FaTimesCircle, FaExclamationTriangle, FaClock, FaSearch,
  FaBan, FaEye, FaDownload, FaChevronLeft, FaFilter,
  FaCalendarAlt, FaTimes, FaHistory, FaCog, FaInbox
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../components/ToastContext';
import NfseConfigModal from '../components/NfseConfigModal';
import config from '../config/config';

// ─────────────────────────────────────────────────────────────────────────

const LIMITE_FILA = 50;

// ─────────────────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  PENDENTE: { label: 'Pendente', color: '#f39c12', bg: 'rgba(243, 156, 18, 0.12)', icon: FaClock },
  SUCESSO: { label: 'Registrada com sucesso', color: '#27ae60', bg: 'rgba(39, 174, 96, 0.12)', icon: FaCheckCircle },
  ERRO: { label: 'Erro / Rejeitada', color: '#e74c3c', bg: 'rgba(231, 76, 60, 0.12)', icon: FaTimesCircle },
  CANCELADA: { label: 'Cancelada', color: '#7f8c8d', bg: 'rgba(127, 140, 141, 0.12)', icon: FaBan },
};

const formatMoeda = (v) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const formatDataHora = (iso) => new Date(iso).toLocaleString('pt-BR');

// yyyy-MM-dd em horário local (não usar toISOString: converte pra UTC e pode
// voltar o dia anterior perto da meia-noite) - mesmo formato aceito pelos
// parâmetros inicio/fim de GET /fiscal/relatorio/*.
const formatarDataISO = (date) => {
  const ano = date.getFullYear();
  const mes = String(date.getMonth() + 1).padStart(2, '0');
  const dia = String(date.getDate()).padStart(2, '0');
  return `${ano}-${mes}-${dia}`;
};

// Baixa um blob de resposta do axios como arquivo - mesmo padrão usado nos
// outros relatórios do sistema (ver Relatorios.jsx, gerarRelatorioDiario).
const baixarBlob = (blob, nomeArquivo) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', nomeArquivo);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const AreaFiscal = () => {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { API_BASE_URL } = config;

  const [abaAtiva, setAbaAtiva] = useState('fila');
  const [fila, setFila] = useState([]);
  const [historico, setHistorico] = useState([]);
  const [carregandoFila, setCarregandoFila] = useState(true);
  const [carregandoHistorico, setCarregandoHistorico] = useState(true);
  const [enviandoFila, setEnviandoFila] = useState(false);

  const [resumoHoje, setResumoHoje] = useState({ totalEmitidas: 0, totalCanceladas: 0, totalErros: 0, totalServicos: 0, totalIssqn: 0 });

  const [filtroStatus, setFiltroStatus] = useState('TODOS');
  const [filtroBusca, setFiltroBusca] = useState('');
  const [filtroDataIni, setFiltroDataIni] = useState('');
  const [filtroDataFim, setFiltroDataFim] = useState('');

  const [periodoInicio, setPeriodoInicio] = useState('');
  const [periodoFim, setPeriodoFim] = useState('');
  const [relatorioPeriodo, setRelatorioPeriodo] = useState(null);
  const [gerandoRelatorioPeriodo, setGerandoRelatorioPeriodo] = useState(false);
  const [exportandoDiario, setExportandoDiario] = useState(false);
  const [exportandoPeriodo, setExportandoPeriodo] = useState(false);

  const [modalLog, setModalLog] = useState(null);

  const [showConfig, setShowConfig] = useState(false);
  const [configExistente, setConfigExistente] = useState(false);
  const [loadingConfig, setLoadingConfig] = useState(true);

  const getAuthHeader = () => ({ headers: { 'Authorization': `Bearer ${localStorage.getItem('jwtToken')}` } });

  // ── Serviço de Configuração Fiscal (usando axios e API_BASE_URL) ──────
  const NFSE_CONFIG_URL = '/empresas/nfse-config';

  const nfseConfigService = {
    getConfig: async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}${NFSE_CONFIG_URL}`, getAuthHeader());
        return response.data;
      } catch (error) {
        if (error.response?.status === 404) {
          return null;
        }
        throw error;
      }
    },

    saveConfig: async (dados) => {
      const response = await axios.put(`${API_BASE_URL}${NFSE_CONFIG_URL}`, dados, getAuthHeader());
      return response.data;
    },

    deleteConfig: async () => {
      await axios.delete(`${API_BASE_URL}${NFSE_CONFIG_URL}`, getAuthHeader());
    }
  };

  // ── Serviço da Área Fiscal (fila, histórico, relatórios) ─────────────
  const fiscalService = {
    getFila: async () => (await axios.get(`${API_BASE_URL}/fiscal/fila`, getAuthHeader())).data,
    getHistorico: async () => (await axios.get(`${API_BASE_URL}/fiscal/historico`, getAuthHeader())).data,
    enviarFila: async () => (await axios.post(`${API_BASE_URL}/fiscal/fila/enviar`, null, getAuthHeader())).data,
    removerDaFila: async (idOrdemServico) => {
      await axios.delete(`${API_BASE_URL}/fiscal/fila/${idOrdemServico}`, getAuthHeader());
    },
    getRelatorioDiario: async () => (await axios.get(`${API_BASE_URL}/fiscal/relatorio/diario`, getAuthHeader())).data,
    getRelatorioPeriodo: async (inicio, fim) =>
      (await axios.get(`${API_BASE_URL}/fiscal/relatorio/periodo`, { ...getAuthHeader(), params: { inicio, fim } })).data,
    getRelatorioPdf: async (inicio, fim) =>
      (await axios.get(`${API_BASE_URL}/fiscal/relatorio/pdf`, { ...getAuthHeader(), params: { inicio, fim }, responseType: 'blob' })).data,
  };

  const tratarErroSessao = (error) => {
    if (error.response && error.response.status === 401) {
      window.location.href = "/login";
      return true;
    }
    return false;
  };

  const carregarFila = async () => {
    setCarregandoFila(true);
    try {
      setFila(await fiscalService.getFila());
    } catch (error) {
      if (!tratarErroSessao(error)) {
        addToast('error', 'Não foi possível carregar a fila de envio.');
      }
    } finally {
      setCarregandoFila(false);
    }
  };

  const carregarHistorico = async () => {
    setCarregandoHistorico(true);
    try {
      setHistorico(await fiscalService.getHistorico());
    } catch (error) {
      if (!tratarErroSessao(error)) {
        addToast('error', 'Não foi possível carregar o histórico.');
      }
    } finally {
      setCarregandoHistorico(false);
    }
  };

  const carregarResumoHoje = async () => {
    try {
      setResumoHoje(await fiscalService.getRelatorioDiario());
    } catch (error) {
      tratarErroSessao(error);
    }
  };

  // ── Carregar configuração fiscal e dados da Área Fiscal ao iniciar ───
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const config = await nfseConfigService.getConfig();
        setConfigExistente(!!config);
      } catch (error) {
        if (!tratarErroSessao(error)) {
          setConfigExistente(false);
        }
      } finally {
        setLoadingConfig(false);
      }
    };
    loadConfig();
    carregarFila();
    carregarHistorico();
    carregarResumoHoje();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filaOcupacaoPct = Math.round((fila.length / LIMITE_FILA) * 100);
  const filaProximaDoLimite = fila.length >= LIMITE_FILA * 0.8;
  const filaCheia = fila.length >= LIMITE_FILA;

  // ── Ações da fila ──────────────────────────────────────────────────────
  const handleEnviarFilaManual = async () => {
    if (fila.length === 0) {
      addToast('warning', 'Não há itens na fila para enviar.');
      return;
    }
    setEnviandoFila(true);
    try {
      const resultado = await fiscalService.enviarFila();
      if (resultado.enviados > 0) {
        addToast('success', `${resultado.enviados} DPS enviada(s) com sucesso.`);
      }
      if (resultado.comErro > 0) {
        addToast('warning', `${resultado.comErro} item(ns) continuam com erro na fila.`);
      }
      await Promise.all([carregarFila(), carregarHistorico(), carregarResumoHoje()]);
    } catch (error) {
      if (!tratarErroSessao(error)) {
        addToast('error', error.response?.data?.message || 'Erro ao enviar a fila.');
      }
    } finally {
      setEnviandoFila(false);
    }
  };

  const handleRemoverDaFila = async (idOrdemServico) => {
    try {
      await fiscalService.removerDaFila(idOrdemServico);
      addToast('success', 'Item removido da fila (ordem segue aberta, sem emissão de NFS-e).');
      await carregarFila();
    } catch (error) {
      if (!tratarErroSessao(error)) {
        addToast('error', error.response?.data?.message || 'Erro ao remover da fila.');
      }
    }
  };

  // ── Relatório por período ─────────────────────────────────────────────
  const handleGerarRelatorioPeriodo = async () => {
    if (!periodoInicio || !periodoFim) {
      addToast('warning', 'Selecione as datas de início e fim do período.');
      return;
    }
    setGerandoRelatorioPeriodo(true);
    try {
      setRelatorioPeriodo(await fiscalService.getRelatorioPeriodo(periodoInicio, periodoFim));
    } catch (error) {
      if (!tratarErroSessao(error)) {
        addToast('error', error.response?.data?.message || 'Erro ao gerar relatório do período.');
      }
    } finally {
      setGerandoRelatorioPeriodo(false);
    }
  };

  // ── Exportação em PDF dos relatórios - o PDF é gerado no backend
  // (PdfService.generateRelatorioFiscalPdf), com o mesmo layout profissional
  // usado nos demais relatórios do sistema (cabeçalho, seções, tabelas e
  // totais) - front só baixa o blob, igual Relatorios.jsx faz pros outros
  // relatórios em PDF. ────────────────────────────────────────────────────
  const handleExportarRelatorioDiario = async () => {
    setExportandoDiario(true);
    try {
      const hoje = formatarDataISO(new Date());
      const blob = await fiscalService.getRelatorioPdf(hoje, hoje);
      baixarBlob(blob, `relatorio-fiscal-nfse_${hoje}.pdf`);
    } catch (error) {
      if (!tratarErroSessao(error)) {
        addToast('error', 'Erro ao gerar o PDF do relatório diário.');
      }
    } finally {
      setExportandoDiario(false);
    }
  };

  const handleExportarRelatorioPeriodo = async () => {
    if (!periodoInicio || !periodoFim) {
      addToast('warning', 'Selecione as datas de início e fim do período.');
      return;
    }
    setExportandoPeriodo(true);
    try {
      const blob = await fiscalService.getRelatorioPdf(periodoInicio, periodoFim);
      baixarBlob(blob, `relatorio-fiscal-nfse_${periodoInicio}_a_${periodoFim}.pdf`);
    } catch (error) {
      if (!tratarErroSessao(error)) {
        addToast('error', 'Erro ao gerar o PDF da apuração do período.');
      }
    } finally {
      setExportandoPeriodo(false);
    }
  };

  // ── Filtro do histórico ───────────────────────────────────────────────
  const historicoFiltrado = useMemo(() => {
    return historico.filter(h => {
      if (filtroStatus !== 'TODOS' && h.status !== filtroStatus) return false;
      if (filtroBusca && !`${h.cliente} ${h.idOrdemServico} ${h.nfseNumero || ''} ${h.chaveAcesso || ''}`.toLowerCase().includes(filtroBusca.toLowerCase())) return false;
      if (filtroDataIni && new Date(h.dataEnvio) < new Date(filtroDataIni)) return false;
      if (filtroDataFim && new Date(h.dataEnvio) > new Date(filtroDataFim + 'T23:59:59')) return false;
      return true;
    });
  }, [historico, filtroStatus, filtroBusca, filtroDataIni, filtroDataFim]);

  // ── Callback para quando a configuração for atualizada ──────────────
  const handleConfigUpdated = (config) => {
    setConfigExistente(!!config);
    if (config) {
      addToast('success', 'Configuração fiscal salva com sucesso!');
    }
  };

  return (
    <PageBackground>
      <PageContainer>
        <TopBar>
          <BackButton onClick={() => navigate('/home')}>
            <FaChevronLeft size={14} /> Voltar
          </BackButton>
          <TitleBlock>
            <FaFileInvoiceDollar size={24} color="#008080" />
            <div>
              <PageTitle>Área Fiscal</PageTitle>
              <PageSubtitle>NFS-e Nacional · Emissão via DPS</PageSubtitle>
            </div>
          </TitleBlock>
          <ConfigButton onClick={() => setShowConfig(true)}>
            <FaCog size={16} /> Configurações
            {!loadingConfig && !configExistente && (
              <ConfigWarningBadge>
                <FaExclamationTriangle size={10} />
              </ConfigWarningBadge>
            )}
          </ConfigButton>
        </TopBar>

        {/* ── Cards de resumo do dia ───────────────────────────────── */}
        <SummaryGrid>
          <SummaryCard accent="#27ae60">
            <SummaryIcon bg="rgba(39, 174, 96, 0.12)" color="#27ae60"><FaCheckCircle size={18} /></SummaryIcon>
            <SummaryValue>{resumoHoje.totalEmitidas}</SummaryValue>
            <SummaryLabel>NFS-e emitidas hoje</SummaryLabel>
          </SummaryCard>
          <SummaryCard accent="#e74c3c">
            <SummaryIcon bg="rgba(231, 76, 60, 0.12)" color="#e74c3c"><FaTimesCircle size={18} /></SummaryIcon>
            <SummaryValue>{resumoHoje.totalErros}</SummaryValue>
            <SummaryLabel>Erros hoje</SummaryLabel>
          </SummaryCard>
          <SummaryCard accent="#008080">
            <SummaryIcon bg="rgba(0, 128, 128, 0.12)" color="#008080"><FaFileInvoiceDollar size={18} /></SummaryIcon>
            <SummaryValue>{formatMoeda(resumoHoje.totalServicos)}</SummaryValue>
            <SummaryLabel>Total faturado hoje</SummaryLabel>
          </SummaryCard>
          <SummaryCard accent="#f39c12">
            <SummaryIcon bg="rgba(243, 156, 18, 0.12)" color="#f39c12"><FaFileInvoiceDollar size={18} /></SummaryIcon>
            <SummaryValue>{formatMoeda(resumoHoje.totalIssqn)}</SummaryValue>
            <SummaryLabel>ISSQN (4%) devido hoje</SummaryLabel>
          </SummaryCard>
        </SummaryGrid>

        {/* ── Abas ──────────────────────────────────────────────────── */}
        <Tabs>
          <TabButton active={abaAtiva === 'fila'} onClick={() => setAbaAtiva('fila')}>
            <FaInbox size={14} /> Fila de Envio
            {fila.length > 0 && <TabBadge alert={filaProximaDoLimite}>{fila.length}</TabBadge>}
          </TabButton>
          <TabButton active={abaAtiva === 'historico'} onClick={() => setAbaAtiva('historico')}>
            <FaHistory size={14} /> Enviados / Histórico
          </TabButton>
          <TabButton active={abaAtiva === 'relatorios'} onClick={() => setAbaAtiva('relatorios')}>
            <FaFileInvoiceDollar size={14} /> Relatórios
          </TabButton>
        </Tabs>

        {/* ── ABA: FILA DE ENVIO ───────────────────────────────────── */}
        {abaAtiva === 'fila' && (
          <Card>
            <CardHeader>
              <div>
                <CardTitle>Fila de espera para envio</CardTitle>
                <CardSubtitle>Itens aguardando geração e envio da DPS ao ambiente nacional</CardSubtitle>
              </div>
              <SendButton onClick={handleEnviarFilaManual} disabled={enviandoFila || fila.length === 0}>
                {enviandoFila ? <><FaSyncAlt className="spin" size={14} /> Enviando...</> : <><FaPaperPlane size={14} /> Enviar fila agora</>}
              </SendButton>
            </CardHeader>

            <QueueMeterWrap>
              <QueueMeterBar>
                <QueueMeterFill pct={filaOcupacaoPct} alert={filaProximaDoLimite} />
              </QueueMeterBar>
              <QueueMeterLabel alert={filaProximaDoLimite}>
                {fila.length} / {LIMITE_FILA} na fila
                {filaCheia && ' — limite atingido, envie para liberar espaço'}
                {!filaCheia && filaProximaDoLimite && ' — perto do limite'}
              </QueueMeterLabel>
            </QueueMeterWrap>

            {carregandoFila ? (
              <EmptyState>
                <FaSyncAlt className="spin" size={28} color="#c5d9d8" />
                <p>Carregando fila...</p>
              </EmptyState>
            ) : fila.length === 0 ? (
              <EmptyState>
                <FaInbox size={32} color="#c5d9d8" />
                <p>Nenhum item na fila no momento.</p>
              </EmptyState>
            ) : (
              <Table>
                <thead>
                  <tr>
                    <th>Ordem de Serviço</th>
                    <th>Cliente</th>
                    <th>Serviço</th>
                    <th>Valor</th>
                    <th>Motivo da falha</th>
                    <th>Criado em</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {fila.map(item => (
                    <tr key={item.idOrdemServico}>
                      <td>OS #{item.idOrdemServico}</td>
                      <td>{item.cliente}</td>
                      <td>{item.servico}</td>
                      <td>{formatMoeda(item.valor)}</td>
                      <td><MutedSmall title={item.erro}>{item.erro}</MutedSmall></td>
                      <td>{formatDataHora(item.criadoEm)}</td>
                      <td>
                        <IconButton title="Remover da fila (finalizar sem NFS-e)" onClick={() => handleRemoverDaFila(item.idOrdemServico)}>
                          <FaTimes size={13} color="#e74c3c" />
                        </IconButton>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card>
        )}

        {/* ── ABA: HISTÓRICO ───────────────────────────────────────── */}
        {abaAtiva === 'historico' && (
          <Card>
            <CardHeader>
              <div>
                <CardTitle>Itens enviados</CardTitle>
                <CardSubtitle>Status de registro na plataforma nacional e ações de correção</CardSubtitle>
              </div>
            </CardHeader>

            <FilterBar>
              <FilterInputWrap>
                <FaSearch size={13} color="#95a5a6" />
                <FilterInput
                  placeholder="Buscar por cliente, OS ou número da NFS-e..."
                  value={filtroBusca}
                  onChange={(e) => setFiltroBusca(e.target.value)}
                />
              </FilterInputWrap>
              <FilterSelect value={filtroStatus} onChange={(e) => setFiltroStatus(e.target.value)}>
                <option value="TODOS">Todos os status</option>
                <option value="SUCESSO">Registrada com sucesso</option>
                <option value="ERRO">Erro / Rejeitada</option>
                <option value="CANCELADA">Cancelada</option>
              </FilterSelect>
              <FilterDateWrap>
                <FaCalendarAlt size={12} color="#95a5a6" />
                <FilterDate type="date" value={filtroDataIni} onChange={(e) => setFiltroDataIni(e.target.value)} />
                <span>até</span>
                <FilterDate type="date" value={filtroDataFim} onChange={(e) => setFiltroDataFim(e.target.value)} />
              </FilterDateWrap>
            </FilterBar>

            {carregandoHistorico ? (
              <EmptyState>
                <FaSyncAlt className="spin" size={28} color="#c5d9d8" />
                <p>Carregando histórico...</p>
              </EmptyState>
            ) : historicoFiltrado.length === 0 ? (
              <EmptyState>
                <FaFilter size={32} color="#c5d9d8" />
                <p>Nenhum resultado para os filtros aplicados.</p>
              </EmptyState>
            ) : (
              <Table>
                <thead>
                  <tr>
                    <th>DPS / NFS-e</th>
                    <th>Cliente</th>
                    <th>Serviço</th>
                    <th>Valor</th>
                    <th>ISSQN</th>
                    <th>Status</th>
                    <th>Envio</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {historicoFiltrado.map(item => {
                    const cfg = STATUS_CONFIG[item.status] || STATUS_CONFIG.SUCESSO;
                    const StatusIcon = cfg.icon;
                    return (
                      <tr key={item.idOrdemServico}>
                        <td>
                          <strong>OS #{item.idOrdemServico}</strong>
                          {item.nfseNumero && <MutedSmall>NFS-e nº {item.nfseNumero}</MutedSmall>}
                        </td>
                        <td>{item.cliente}</td>
                        <td>{item.servico}</td>
                        <td>{formatMoeda(item.valor)}</td>
                        <td>{formatMoeda(item.issqn)}</td>
                        <td>
                          <StatusBadge color={cfg.color} bg={cfg.bg}>
                            <StatusIcon size={11} /> {cfg.label}
                          </StatusBadge>
                        </td>
                        <td>{formatDataHora(item.dataEnvio)}</td>
                        <td>
                          <ActionsCell>
                            <IconButton title="Ver log de envio" onClick={() => setModalLog(item)}>
                              <FaEye size={13} color="#008080" />
                            </IconButton>
                          </ActionsCell>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            )}
          </Card>
        )}

        {/* ── ABA: RELATÓRIOS ──────────────────────────────────────── */}
        {abaAtiva === 'relatorios' && (
          <Card>
            <CardHeader>
              <div>
                <CardTitle>Relatórios</CardTitle>
                <CardSubtitle>Totais diários e apuração de ISSQN para conferência da guia</CardSubtitle>
              </div>
            </CardHeader>

            <ReportGrid>
              <ReportBlock>
                <ReportBlockTitle>Relatório diário</ReportBlockTitle>
                <ReportRow><span>NFS-e emitidas com sucesso</span><strong>{resumoHoje.totalEmitidas}</strong></ReportRow>
                <ReportRow><span>NFS-e canceladas</span><strong>{resumoHoje.totalCanceladas}</strong></ReportRow>
                <ReportRow><span>Erros / rejeições</span><strong>{resumoHoje.totalErros}</strong></ReportRow>
                <ReportRow><span>Total de serviços prestados</span><strong>{formatMoeda(resumoHoje.totalServicos)}</strong></ReportRow>
                <ReportRow highlight><span>ISSQN devido (4%)</span><strong>{formatMoeda(resumoHoje.totalIssqn)}</strong></ReportRow>
                <ReportActionButton onClick={handleExportarRelatorioDiario} disabled={exportandoDiario}>
                  <FaDownload size={13} className={exportandoDiario ? 'spin' : ''} /> {exportandoDiario ? 'Gerando PDF...' : 'Exportar PDF completo'}
                </ReportActionButton>
              </ReportBlock>

              <ReportBlock>
                <ReportBlockTitle>Apuração por período</ReportBlockTitle>
                <FilterDateWrap style={{ marginBottom: '14px' }}>
                  <FaCalendarAlt size={12} color="#95a5a6" />
                  <FilterDate type="date" value={periodoInicio} onChange={(e) => setPeriodoInicio(e.target.value)} />
                  <span>até</span>
                  <FilterDate type="date" value={periodoFim} onChange={(e) => setPeriodoFim(e.target.value)} />
                </FilterDateWrap>
                <ReportRow><span>Notas emitidas</span><strong>{relatorioPeriodo ? relatorioPeriodo.notasEmitidas : '—'}</strong></ReportRow>
                <ReportRow><span>Notas canceladas</span><strong>{relatorioPeriodo ? relatorioPeriodo.notasCanceladas : '—'}</strong></ReportRow>
                <ReportRow><span>Base de cálculo (vServ)</span><strong>{relatorioPeriodo ? formatMoeda(relatorioPeriodo.baseCalculo) : '—'}</strong></ReportRow>
                <ReportRow highlight><span>ISSQN a recolher no período</span><strong>{relatorioPeriodo ? formatMoeda(relatorioPeriodo.issqnTotal) : '—'}</strong></ReportRow>
                <ReportActionButton onClick={handleGerarRelatorioPeriodo} disabled={gerandoRelatorioPeriodo}>
                  <FaSyncAlt size={13} className={gerandoRelatorioPeriodo ? 'spin' : ''} /> {gerandoRelatorioPeriodo ? 'Gerando...' : 'Gerar relatório do período'}
                </ReportActionButton>
                <ReportActionButton
                  style={{ marginTop: '8px' }}
                  onClick={handleExportarRelatorioPeriodo}
                  disabled={exportandoPeriodo || !periodoInicio || !periodoFim}
                >
                  <FaDownload size={13} className={exportandoPeriodo ? 'spin' : ''} /> {exportandoPeriodo ? 'Gerando PDF...' : 'Exportar PDF completo'}
                </ReportActionButton>
              </ReportBlock>
            </ReportGrid>
          </Card>
        )}
      </PageContainer>

      {/* ── MODAL: LOG DE ENVIO ────────────────────────────────────── */}
      {modalLog && (
        <ModalOverlay onClick={() => setModalLog(null)}>
          <ModalBox onClick={(e) => e.stopPropagation()}>
            <ModalHeaderBar>
              <h3>Log de envio — OS #{modalLog.idOrdemServico}</h3>
              <CloseX onClick={() => setModalLog(null)}><FaTimes /></CloseX>
            </ModalHeaderBar>
            <ModalBody>
              <LogList>
                {(modalLog.logs || []).map((log, i) => (
                  <LogEntry key={i}>
                    <LogTime>{formatDataHora(log.data)}</LogTime>
                    <LogText>{log.evento}</LogText>
                  </LogEntry>
                ))}
              </LogList>
            </ModalBody>
          </ModalBox>
        </ModalOverlay>
      )}

      {/* ── MODAL: CONFIGURAÇÕES FISCAIS ──────────────────────────── */}
      <NfseConfigModal
        isOpen={showConfig}
        onClose={() => setShowConfig(false)}
        onConfigUpdated={handleConfigUpdated}
        apiBaseUrl={API_BASE_URL}
      />
    </PageBackground>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// ESTILOS — segue o mesmo design system do Home.jsx (teal #008080)
// ─────────────────────────────────────────────────────────────────────────

const PageBackground = styled.div`
  background: linear-gradient(135deg, #e6f7f5 0%, #d4efed 100%);
  min-height: 100vh;
  display: flex;
  justify-content: center;
`;

const PageContainer = styled.div`
  width: 100%;
  max-width: 1400px;
  padding: 20px;
  box-sizing: border-box;
  @media (max-width: 768px) { padding: 12px; }
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border-radius: 15px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  padding: 16px 20px;
  margin-bottom: 20px;
  flex-wrap: wrap;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: #f1f5f5;
  border: none;
  color: #2c3e50;
  padding: 9px 14px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
  &:hover { background: #e2ebeb; }
`;

const TitleBlock = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 200px;
`;

const PageTitle = styled.h1`
  margin: 0;
  font-size: 1.3rem;
  color: #004d4d;
  font-weight: 700;
`;

const PageSubtitle = styled.p`
  margin: 2px 0 0 0;
  font-size: 0.78rem;
  color: #7f8c8d;
`;

const ConfigButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: #f1f5f5;
  border: none;
  color: #2c3e50;
  padding: 9px 14px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.82rem;
  cursor: pointer;
  position: relative;
  &:hover { background: #e2ebeb; }
`;

const ConfigWarningBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #e74c3c;
  color: #fff;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  font-size: 0.65rem;
  margin-left: 4px;
  animation: pulse 2s infinite;
  
  @keyframes pulse {
    0% { transform: scale(1); }
    50% { transform: scale(1.1); }
    100% { transform: scale(1); }
  }
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 14px;
  margin-bottom: 20px;
`;

const SummaryCard = styled.div`
  background: #fff;
  border-radius: 14px;
  padding: 16px 18px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
  border-left: 4px solid ${p => p.accent};
`;

const SummaryIcon = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: ${p => p.bg};
  color: ${p => p.color};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 10px;
`;

const SummaryValue = styled.div`
  font-size: 1.4rem;
  font-weight: 700;
  color: #2c3e50;
`;

const SummaryLabel = styled.div`
  font-size: 0.76rem;
  color: #7f8c8d;
  font-weight: 500;
  margin-top: 2px;
`;

const Tabs = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const TabButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  border-radius: 10px;
  border: none;
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
  background: ${p => p.active ? 'linear-gradient(135deg, #008080 0%, #20b2aa 100%)' : '#fff'};
  color: ${p => p.active ? '#fff' : '#2c3e50'};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  transition: all 0.2s ease;
`;

const TabBadge = styled.span`
  background: ${p => p.alert ? '#e74c3c' : 'rgba(255,255,255,0.3)'};
  color: #fff;
  font-size: 0.7rem;
  padding: 1px 7px;
  border-radius: 10px;
  font-weight: 700;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 15px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  padding: 22px;
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 18px;
`;

const CardTitle = styled.h2`
  margin: 0;
  font-size: 1.05rem;
  color: #004d4d;
  font-weight: 700;
`;

const CardSubtitle = styled.p`
  margin: 4px 0 0 0;
  font-size: 0.8rem;
  color: #7f8c8d;
`;

const SendButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 11px 20px;
  background: ${p => p.disabled ? '#a9c7c7' : 'linear-gradient(135deg, #008080 0%, #20b2aa 100%)'};
  color: #fff;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.85rem;
  cursor: ${p => p.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  &:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 4px 14px rgba(0, 128, 128, 0.35); }
  .spin { animation: spin 1s linear infinite; }
  @keyframes spin { to { transform: rotate(360deg); } }
`;

const QueueMeterWrap = styled.div`
  margin-bottom: 18px;
`;

const QueueMeterBar = styled.div`
  width: 100%;
  height: 8px;
  border-radius: 5px;
  background: #eef3f3;
  overflow: hidden;
`;

const QueueMeterFill = styled.div`
  height: 100%;
  width: ${p => p.pct}%;
  background: ${p => p.alert ? 'linear-gradient(90deg, #f39c12, #e74c3c)' : 'linear-gradient(90deg, #008080, #20b2aa)'};
  transition: width 0.3s ease;
`;

const QueueMeterLabel = styled.div`
  font-size: 0.76rem;
  color: ${p => p.alert ? '#e74c3c' : '#7f8c8d'};
  font-weight: 600;
  margin-top: 6px;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 50px 20px;
  color: #95a5a6;
  font-size: 0.88rem;
  p { margin: 0; }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.84rem;

  th {
    text-align: left;
    padding: 10px 12px;
    color: #7f8c8d;
    font-weight: 600;
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    border-bottom: 2px solid #eef3f3;
  }
  td {
    padding: 12px;
    border-bottom: 1px solid #f2f6f6;
    color: #2c3e50;
    vertical-align: middle;
  }
  tr:hover td { background: #fafcfc; }
`;

const MutedSmall = styled.div`
  font-size: 0.72rem;
  color: #7f8c8d;
  margin-top: 2px;
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 0.74rem;
  font-weight: 600;
  color: ${p => p.color};
  background: ${p => p.bg};
  white-space: nowrap;
`;

const ActionsCell = styled.div`
  display: flex;
  gap: 6px;
`;

const IconButton = styled.button`
  background: #f4f7f7;
  border: none;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s;
  &:hover { background: #e5eded; }
`;

const FilterBar = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 18px;
`;

const FilterInputWrap = styled.div`
  flex: 2;
  min-width: 220px;
  display: flex;
  align-items: center;
  gap: 8px;
  background: #f4f7f7;
  border-radius: 8px;
  padding: 9px 12px;
`;

const FilterInput = styled.input`
  border: none;
  background: transparent;
  outline: none;
  font-size: 0.84rem;
  width: 100%;
  color: #2c3e50;
`;

const FilterSelect = styled.select`
  padding: 9px 12px;
  border-radius: 8px;
  border: 1px solid #e0e8e8;
  background: #fff;
  font-size: 0.82rem;
  color: #2c3e50;
`;

const FilterDateWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #f4f7f7;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 0.78rem;
  color: #7f8c8d;
`;

const FilterDate = styled.input`
  border: none;
  background: transparent;
  outline: none;
  font-size: 0.8rem;
  color: #2c3e50;
`;

const ReportGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 18px;
`;

const ReportBlock = styled.div`
  background: #fafcfc;
  border: 1px solid #eef3f3;
  border-radius: 12px;
  padding: 18px;
`;

const ReportBlockTitle = styled.h3`
  margin: 0 0 14px 0;
  font-size: 0.92rem;
  color: #004d4d;
  font-weight: 700;
`;

const ReportRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 9px 0;
  border-bottom: 1px solid #eef3f3;
  font-size: 0.85rem;
  color: ${p => p.highlight ? '#008080' : '#2c3e50'};
  font-weight: ${p => p.highlight ? 700 : 500};
`;

const ReportActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  margin-top: 16px;
  padding: 10px;
  background: #fff;
  border: 1.5px solid #008080;
  color: #008080;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.82rem;
  cursor: pointer;
  transition: all 0.2s;
  &:hover:not(:disabled) { background: #008080; color: #fff; }
  &:disabled { opacity: 0.65; cursor: not-allowed; }
  .spin { animation: spin 1s linear infinite; }
  @keyframes spin { to { transform: rotate(360deg); } }
`;

// ── Modais ──────────────────────────────────────────────────────────────

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 16px;
`;

const ModalBox = styled.div`
  background: #fff;
  border-radius: 14px;
  width: 100%;
  max-width: 480px;
  max-height: 85vh;
  overflow: auto;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.25);
`;

const ModalHeaderBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: ${p => p.danger ? 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)' : 'linear-gradient(135deg, #008080 0%, #20b2aa 100%)'};
  color: #fff;
  border-radius: 14px 14px 0 0;
  h3 { margin: 0; font-size: 1rem; }
`;

const CloseX = styled.button`
  background: rgba(255,255,255,0.15);
  border: none;
  color: #fff;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover { background: rgba(255,255,255,0.3); }
`;

const ModalBody = styled.div`
  padding: 20px;
`;

const LogList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const LogEntry = styled.div`
  border-left: 3px solid #008080;
  padding-left: 12px;
`;

const LogTime = styled.div`
  font-size: 0.72rem;
  color: #7f8c8d;
  font-weight: 600;
`;

const LogText = styled.div`
  font-size: 0.85rem;
  color: #2c3e50;
  margin-top: 2px;
`;

export default AreaFiscal;